import useAuthUser from "hooks/auth-user";
import useLanguage from "hooks/language";
import { SCREENING_FLOW, ScreeningFlow } from "models/screening-flow";
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { ReactComponent as HpvIcon } from "../../assets/images/hpv-screening.svg";
import { ReactComponent as StiScreeningIcon } from "../../assets/images/sti-screening.svg";
import {
    clearInProgressBookingSelections,
    getOtherScreeningFlow,
    getScreeningFlowSwitchBlockReason,
} from "utils/screening-flow-switch";
import routes from "utils/routes";
import { getScreeningFlow, setScreeningFlow } from "utils/session-storage";
import BookingFlowScreeningSwitchModal from "./booking-flow-screening-switch-modal";
import styles from "./booking-flow-screening-indicator.module.css";

const BookingFlowScreeningIndicator: React.FunctionComponent = () => {
    const { translations } = useLanguage();
    const [authUser] = useAuthUser();
    const history = useHistory();
    const stripRef = useRef<HTMLDivElement>(null);

    const screeningFlow = getScreeningFlow();
    const isCervical = screeningFlow === SCREENING_FLOW.HPV;
    const t = translations.bookingFlow.screeningIndicator;
    const tSwitch = translations.bookingFlow.screeningSwitch;

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [pendingFlow, setPendingFlow] = useState<ScreeningFlow | null>(null);

    const otherFlow = getOtherScreeningFlow(screeningFlow);
    const otherIsCervical = otherFlow === SCREENING_FLOW.HPV;
    const otherLabel = otherIsCervical ? t.cervicalCancer : t.stbbiScreening;
    const otherBlockReason = getScreeningFlowSwitchBlockReason(
        authUser,
        otherFlow
    );
    const isOtherDisabled = otherBlockReason !== undefined;

    useEffect(() => {
        if (!isDropdownOpen) {
            return;
        }

        const handlePointerDown = (event: MouseEvent) => {
            if (
                stripRef.current &&
                !stripRef.current.contains(event.target as Node)
            ) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
        };
    }, [isDropdownOpen]);

    const toggleDropdown = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDropdownOpen((open) => !open);
    };

    const openConfirmForOtherFlow = () => {
        if (isOtherDisabled) {
            return;
        }
        setPendingFlow(otherFlow);
        setIsDropdownOpen(false);
        setIsConfirmOpen(true);
    };

    const cancelSwitch = () => {
        setIsConfirmOpen(false);
        setPendingFlow(null);
    };

    const confirmSwitch = () => {
        if (!pendingFlow) {
            return;
        }
        clearInProgressBookingSelections();
        setScreeningFlow(pendingFlow);
        setIsConfirmOpen(false);
        setPendingFlow(null);
        history.push(routes.AVAILABILITY, { screeningFlowSwitched: true });
    };

    const pendingLabel =
        pendingFlow === SCREENING_FLOW.HPV
            ? t.cervicalCancer
            : t.stbbiScreening;

    return (
        <>
            <div ref={stripRef} className={styles.strip}>
                <div className={styles.main}>
                    {isCervical ? (
                        <HpvIcon className={styles.icon} aria-hidden />
                    ) : (
                        <StiScreeningIcon className={styles.icon} aria-hidden />
                    )}
                    <span className={styles.label}>
                        {isCervical ? t.cervicalCancer : t.stbbiScreening}
                    </span>
                </div>
                <button
                    type="button"
                    className={styles.changeButton}
                    onClick={toggleDropdown}
                    onMouseDown={(event) => event.stopPropagation()}
                    aria-expanded={isDropdownOpen}
                >
                    {t.change}
                </button>

                {isDropdownOpen && (
                    <div className={styles.dropdown}>
                        <button
                            type="button"
                            className={`${styles.dropdownOption} ${
                                isOtherDisabled
                                    ? styles.dropdownOptionDisabled
                                    : ""
                            }`}
                            onClick={openConfirmForOtherFlow}
                            disabled={isOtherDisabled}
                            aria-disabled={isOtherDisabled}
                        >
                            {otherIsCervical ? (
                                <HpvIcon
                                    className={styles.dropdownIcon}
                                    aria-hidden
                                />
                            ) : (
                                <StiScreeningIcon
                                    className={styles.dropdownIcon}
                                    aria-hidden
                                />
                            )}
                            <span className={styles.dropdownLabel}>
                                {otherLabel}
                            </span>
                        </button>
                    </div>
                )}
            </div>

            {isConfirmOpen && pendingFlow && (
                <BookingFlowScreeningSwitchModal
                    title={tSwitch.title}
                    bodyHtml={tSwitch.body.replace(
                        "{serviceName}",
                        pendingLabel
                    )}
                    confirmLabel={tSwitch.confirm}
                    cancelLabel={tSwitch.cancel}
                    onConfirm={confirmSwitch}
                    onCancel={cancelSwitch}
                />
            )}
        </>
    );
};

export default BookingFlowScreeningIndicator;
